import { AsyncThunkPayloadCreator, createAsyncThunk } from '@reduxjs/toolkit';
import { replace } from 'connected-react-router';
import { debounce } from 'lodash';

import { api } from '../../../../web-ui-jobs/src/ducks/filterBar';
import { ACTION_BASE } from '../constants';
import { VisualAppState } from '../../reducer';
import * as selectors from './selectors';
import { convertStringToFullTextSearch, parseSavedFilters, serializeFilters } from './utils';
import type { OwnerFilterItem } from './types';
import { VISUAL_SLICE_NAME } from './constants';

const { getTeams, getUsers, getUser } = api;

export const loadOwners = createAsyncThunk(
  `${ACTION_BASE}/loadOwners`,
  async (__, thunkAPI) => {
    const state = thunkAPI.getState() as VisualAppState;
    const { searchValue } = state.apps.visual.filterBar.owner;

    const results = await Promise.all(
      [
        getUsers({
          limit: 100,
          ...(searchValue ? { phrase: searchValue } : {}),
        }),
        getTeams({
          limit: 100,
          offset: 0,
          ...(searchValue ? { name: searchValue } : {}),
        }),
      ],
    );

    const [userResults, teamResults] = results;

    const users: OwnerFilterItem[] = userResults.data.results.map((user) => ({
      label: user.username ?? user.id,
      key: user.id,
      type: 'user',
    }));

    const teams: OwnerFilterItem[] = teamResults.data.results.map((team) => ({
      label: team.name ?? team.id,
      key: team.id,
      type: 'team',
    }));

    return users.concat(teams);
  },
);

export const initFilters = createAsyncThunk(
  `${ACTION_BASE}/initOwners`,
  async (arg, thunkAPI) => {
    const state = thunkAPI.getState() as VisualAppState;

    const savedFilterState = parseSavedFilters(selectors.location(state).search);
    const currentUser = selectors.getUser(state).toJS();

    let loadedUser: OwnerFilterItem | undefined;

    // Query for the user that is saved in params / localStorage.
    if (
      savedFilterState.ownerType === 'user' &&
      savedFilterState.ownerId &&
      savedFilterState.ownerId !== currentUser.user_id
    ) {
      try {
        const response = await getUser({
          id: savedFilterState.ownerId,
        }).then((res) => res.data);

        if (response.id) {
          loadedUser = {
            key: response.id,
            label: response.username ?? response.id,
            type: 'user',
          };
        }
        // eslint-disable-next-line no-empty
      } catch {}
    }

    void thunkAPI.dispatch(loadOwners());

    return {
      user: state.user,
      params: savedFilterState,
      loadedUser,
    };
  },
);

export const selectOwnerByUserId = createAsyncThunk(
  `${ACTION_BASE}/selectOwner`,
  async (id: string, thunkAPI) => {
    const state = thunkAPI.getState() as VisualAppState;
    const { owner: { items } } = selectors.visualFilters(state);

    const localOwner = items.find((owner) => (
      owner.key === id && owner.type === 'user'
    ));

    // Use cached / local owner if the user is already fetched
    if (localOwner) {
      return localOwner;
    }

    // Else, let's query the API for that user ID and populate if possible
    const owner = await api.getUser({ id })
      .then(({ data }) => ({
        type: 'user',
        label: data.username,
        key: data.id,
      } as OwnerFilterItem))
      .catch(() => null);

    return owner;
  },
);

export const saveFilters = createAsyncThunk(
  `${ACTION_BASE}/saveFilters`,
  (__, thunkAPI) => {
    const state = thunkAPI.getState() as VisualAppState;

    const serializedFilters = serializeFilters(
      selectors.visualFilters(state),
    );

    localStorage[VISUAL_SLICE_NAME] = JSON.stringify(serializedFilters);

    const location = selectors.location(state);

    thunkAPI.dispatch(replace({
      ...location,
      search: new URLSearchParams(serializedFilters).toString(),
    }));
  },
);

export const updateBuildNameSearch = createAsyncThunk(
  `${ACTION_BASE}/updateBuildNameSearch`,
  (value: string) => (
    value ? convertStringToFullTextSearch(value) : null
  ),
);

/**
 * Debounced handler exclusively for triggering our _actual_ thunk to update the search value.
 * Needs to be done separately, so we can handle the promise result for the correct (above) one.
 */
export const dbUpdateBuildNameSearch = createAsyncThunk(
  `${ACTION_BASE}/dbUpdateBuildNameSearch`,
  debounce<AsyncThunkPayloadCreator<void, string>>(
    (
      (value, thunkAPI) => {
        if (
          value.length === 0 ||
          value.length >= 3
        ) {
          void thunkAPI.dispatch(updateBuildNameSearch(value));
        }
      }
    ),
    600, { trailing: true },
  ),
);

/**
 * Updates the search value for build search parameters.
 */
export const updateBuildName = createAsyncThunk(
  `${ACTION_BASE}/updateBuildName`,
  (value: string, thunkAPI) => {
    void thunkAPI.dispatch(dbUpdateBuildNameSearch(value));
  },
);
